import React, { useState, useEffect, useRef, useContext } from 'react';
import AppContext from '../api/AppContext';
import api from '../services/api';
import EmojiPicker from 'emoji-picker-react';
import { FaPaperclip, FaTrashAlt, FaPaperPlane, FaArrowLeft, FaSearch, FaSmile } from 'react-icons/fa';

const linkify = (text) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  return parts.map((part, i) => {
    if (urlRegex.test(part)) {
      return <a key={i} href={part} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2', textDecoration: 'underline', wordBreak: 'break-all' }}>{part}</a>;
    }
    return part;
  });
};

const ChatWindow = ({ conversation, onBack }) => {
  const { user, socket } = useContext(AppContext);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [typing, setTyping] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, messageId: null });
  const messagesEndRef = useRef(null);
  const messagesRef = useRef(null);
  const fileInputRef = useRef(null);
  const [pendingFile, setPendingFile] = useState(null); // { url, type, size, mediaType }
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const isAdmin = user?.role === 'admin';
  const isClosed = conversation.status === 'closed';
  const isMemberView = user?.role === 'member';

  useEffect(() => {
    if (!socket) return;

    const joinRoom = () => {
      socket.emit('chat:join', { conversationId: conversation._id });
    };

    if (socket.connected) joinRoom();
    socket.on('connect', joinRoom);
    fetchMessages();

    socket.on('chat:message', handleNewMessage);
    socket.on('chat:typing', handleTyping);
    socket.on('chat:message-deleted', handleMessageDeleted);

    return () => {
      socket.off('connect', joinRoom);
      socket.off('chat:message', handleNewMessage);
      socket.off('chat:typing', handleTyping);
      socket.off('chat:message-deleted', handleMessageDeleted);
    };
  }, [conversation._id, socket]);

  useEffect(() => {
    // Rola até a última mensagem de forma instantânea (scrollIntoView smooth
    // parava no meio quando imagens carregavam depois / layout mudava)
    scrollToBottom(false);
    const t = setTimeout(() => scrollToBottom(false), 150);
    return () => clearTimeout(t);
  }, [messages, conversation._id]);

  useEffect(() => {
    if (conversation._id) scrollToBottom(false);
  }, [conversation._id]);

const fetchMessages = async () => {
  try {
    setLoading(true);
    const response = await api.get(`/chat/conversations/${conversation._id}`);
    // A resposta tem response.data.data.messages (array)
    const msgs = response.data.data.messages || [];
    setMessages(msgs.filter(msg => !msg.deleted));
  } catch (error) {
    console.error('Erro ao carregar mensagens:', error);
  } finally {
    setLoading(false);
  }
};

  const handleNewMessage = (data) => {
    if (data.conversationId === conversation._id) {
      setMessages(prev => [...prev, data.message]);
      if (data.message.sender.userId !== user._id) {
        socket.emit('chat:read', { conversationId: conversation._id, messageIds: [data.message._id] });
      }
    }
  };

  const handleMessageDeleted = ({ conversationId, messageId }) => {
    if (conversationId === conversation._id) {
      setMessages(prev => prev.filter(msg => msg._id !== messageId));
      showToast('Mensagem removida', 'info');
    }
  };

  const handleTyping = (data) => {
    if (data.userId !== user._id) {
      setTyping(data.isTyping);
      if (data.isTyping) setTimeout(() => setTyping(false), 2000);
    }
  };

  // const sendMessage = (e) => {
  //   e.preventDefault();
  //   if (!newMessage.trim()) return;
  //   socket.emit('chat:message', { conversationId: conversation._id, content: newMessage });
  //   setNewMessage('');
  // };
  const sendMessage = (e) => {
    e.preventDefault();

    if (isClosed) return;

    // Impede envio se não houver texto nem arquivo pendente
    if (!newMessage.trim() && !pendingFile) return;

    let finalContent = newMessage.trim();

    // Se houver arquivo pendente, anexa o nome ao final da legenda
    if (pendingFile) {
      const fileNameSuffix = ` [${pendingFile.fileName}]`;
      finalContent = finalContent ? finalContent + fileNameSuffix : pendingFile.fileName;
    }

    const messageData = {
      conversationId: conversation._id,
      content: finalContent,
    };

    if (pendingFile) {
      messageData.mediaUrl = pendingFile.url;
      messageData.mediaType = pendingFile.mediaType;
      messageData.mediaSize = pendingFile.size;
    }

    socket.emit('chat:message', messageData);
    setNewMessage('');
    setPendingFile(null);
  };
  const handleTypingStart = () => socket.emit('chat:typing', { conversationId: conversation._id, isTyping: true });
  const handleTypingStop = () => socket.emit('chat:typing', { conversationId: conversation._id, isTyping: false });

  const scrollToBottom = () => {
    const el = messagesRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };

  // Upload de arquivo (apenas admin)
  // const handleFileSelect = async (e) => {
  //   const file = e.target.files[0];
  //   if (!file) return;
  //   setUploading(true);
  //   const formData = new FormData();
  //   formData.append('file', file);
  //   try {
  //     const response = await api.post('/chat/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  //     const { url, type } = response.data.data;
  //     let mediaType = null;
  //     if (type.startsWith('image/')) mediaType = 'image';
  //     else if (type.startsWith('video/')) mediaType = 'video';
  //     else if (type.startsWith('audio/')) mediaType = 'audio';
  //     else mediaType = 'document';
  //     socket.emit('chat:message', {
  //       conversationId: conversation._id,
  //       content: '',
  //       mediaUrl: url,
  //       mediaType,
  //       mediaSize: file.size
  //     });
  //     showToast('Arquivo enviado com sucesso', 'success');
  //   } catch (err) {
  //     console.error('Erro ao enviar arquivo:', err);
  //     showToast('Erro ao enviar arquivo', 'error');
  //   } finally {
  //     setUploading(false);
  //     fileInputRef.current.value = '';
  //   }
  // };
  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/chat/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const { url, type } = response.data.data;

      let mediaType = null;
      if (type.startsWith('image/')) mediaType = 'image';
      else if (type.startsWith('video/')) mediaType = 'video';
      else if (type.startsWith('audio/')) mediaType = 'audio';
      else mediaType = 'document';

      setPendingFile({
        url,
        type,
        size: file.size,
        mediaType,
        fileName: file.name, // 🆕 nome original
      });

      showToast('Arquivo pronto. Adicione uma legenda e envie.', 'success');
    } catch (err) {
      console.error('Erro ao enviar arquivo:', err);
      showToast('Erro ao enviar arquivo', 'error');
    } finally {
      setUploading(false);
      fileInputRef.current.value = '';
    }
  };
  const handleDeleteMessage = async (messageId) => {
    setDeleteConfirm({ show: true, messageId });
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/chat/messages/${deleteConfirm.messageId}`);
      setDeleteConfirm({ show: false, messageId: null });
      showToast('Mensagem excluída', 'success');
    } catch (err) {
      console.error('Erro ao deletar mensagem:', err);
      showToast('Erro ao excluir mensagem', 'error');
    }
  };

  const showToast = (message, type) => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
  };

  const renderMedia = (msg) => {
    if (!msg.mediaUrl) return null;
    if (msg.mediaType === 'image') return <img src={msg.mediaUrl} alt="imagem" style={styles.mediaImage} onLoad={() => scrollToBottom()} />;
    if (msg.mediaType === 'video') return <video controls src={msg.mediaUrl} style={styles.mediaVideo} />;
    if (msg.mediaType === 'document') return <a href={msg.mediaUrl} target="_blank" rel="noopener noreferrer" style={styles.mediaLink}>📄 Ver documento</a>;
    return null;
  };

  if (loading) return <div style={styles.loading}>Carregando mensagens...</div>;

  return (
    <div style={styles.chatWindow}>
      <div style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {onBack && (
            <button onClick={onBack} style={styles.backButton}>
              <FaArrowLeft />
            </button>
          )}
          <h3 style={{ margin: 0 }}>Conversa #{conversation._id.slice(-6)}</h3>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ position: 'relative' }}>
            <FaSearch style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: '#ffffff', opacity: 0.7, fontSize: '12px' }} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar mensagem..."
              style={styles.searchInputHeader}
            />
          </div>
          <span style={{
            ...styles.status,
            backgroundColor: isClosed ? '#dc3545' : '#28a745',
          }}>{conversation.status}</span>
        </div>
      </div>
      <div style={styles.messages} ref={messagesRef}>
        {messages.filter(msg => !searchTerm.trim() || (msg.content || '').toLowerCase().includes(searchTerm.toLowerCase())).map((msg, idx, filtered) => {
          const isOwn = msg.sender.userId === user._id;
          const isSecretaria = ['staff', 'admin'].includes(msg.sender.role);
          // Mensagens da secretaria (admin/staff) sempre à direita e em outra cor
          const alignRight = isOwn || isSecretaria;
          const bubbleColor = isOwn ? '#DCF8C6' : isSecretaria ? '#cfe3ff' : '#FFF';
          const msgDate = new Date(msg.createdAt);
          const today = new Date();
          const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
          let dateLabel = msgDate.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
          if (msgDate.toDateString() === today.toDateString()) dateLabel = 'Hoje';
          else if (msgDate.toDateString() === yesterday.toDateString()) dateLabel = 'Ontem';
          const prevMsgDate = idx > 0 ? new Date(filtered[idx - 1].createdAt) : null;
          let prevDateLabel = '';
          if (prevMsgDate) {
            prevDateLabel = prevMsgDate.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
            if (prevMsgDate.toDateString() === today.toDateString()) prevDateLabel = 'Hoje';
            else if (prevMsgDate.toDateString() === yesterday.toDateString()) prevDateLabel = 'Ontem';
          }
          const showDate = dateLabel !== prevDateLabel;
          return (
            <React.Fragment key={msg._id}>
              {showDate && (
                <div style={styles.dateSeparator}>
                  <span style={styles.dateLabel}>{dateLabel}</span>
                </div>
              )}
            <div
              style={{
                ...styles.message,
                alignSelf: alignRight ? 'flex-end' : 'flex-start',
                backgroundColor: bubbleColor,
              }}
              className="message-item"
            >
              <div style={styles.messageHeader}>
                <div style={styles.sender}>
                  {isSecretaria
                    ? `Secretaria (${msg.sender.name.split(' ')[0]})`
                    : msg.sender.name}
                </div>
                {isAdmin  && (
                  <button
                    onClick={() => handleDeleteMessage(msg._id)}
                    style={styles.deleteBtn}
                    title="Excluir mensagem"
                  >
                    <FaTrashAlt size={12} />
                  </button>
                )}
              </div>
              {msg.content && <div style={styles.messageContent}>{linkify(msg.content)}</div>}
              {renderMedia(msg)}
              <div style={styles.time}>{new Date(msg.createdAt).toLocaleTimeString()}</div>
            </div>
            </React.Fragment>
          );
        })}
        {typing && <div style={styles.typing}>Alguém está digitando...</div>}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={sendMessage} style={styles.inputArea}>
        <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileSelect} accept="image/*,video/*,application/pdf,application/msword" />
        <button
          type="button"
          onClick={() => fileInputRef.current.click()}
          disabled={isClosed || uploading || !!pendingFile}
          style={styles.attachBtn}
          >
          {uploading ? '⏳' : <FaPaperclip />}
        </button>
        <div style={{ position: 'relative' }}>
          <button
            type="button"
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            disabled={isClosed}
            style={styles.emojiBtn}
            title="Inserir emoji"
          >
            <FaSmile />
          </button>
          {showEmojiPicker && (
            <div style={styles.emojiPicker}>
              <EmojiPicker
                width="100%"
                height={320}
                onEmojiClick={(emojiData) => {
                  setNewMessage(prev => prev + emojiData.emoji);
                  setShowEmojiPicker(false);
                }}
                searchPlaceHolder="Buscar emoji..."
                theme="light"
                previewConfig={{ showPreview: false }}
              />
            </div>
          )}
        </div>
        <input
          type="text"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onFocus={handleTypingStart}
          onBlur={handleTypingStop}
          placeholder={isClosed ? "Atendimento encerrado" : "Digite sua mensagem..."}
          disabled={isClosed}
          style={styles.input}
          />
        <button type="submit" disabled={isClosed} style={styles.sendBtn}><FaPaperPlane /></button>
          {pendingFile && (
            <div style={styles.pendingFile}>
              <span>📎 {pendingFile.mediaType} pronto para envio, adicione legenda</span>
              <button onClick={() => setPendingFile(null)} style={styles.removeFileBtn}>✕</button>
            </div>
          )}
      </form>

      {/* Toast feedback */}
      {toast.show && (
        <div style={{ ...styles.toast, backgroundColor: toast.type === 'error' ? '#dc3545' : '#28a745' }}>
          {toast.message}
        </div>
      )}

      {/* Modal de confirmação de exclusão */}
      {deleteConfirm.show && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3>Confirmar exclusão</h3>
            <p>Tem certeza que deseja excluir esta mensagem permanentemente?</p>
            <div style={styles.modalButtons}>
              <button onClick={() => setDeleteConfirm({ show: false, messageId: null })} style={styles.cancelBtn}>Cancelar</button>
              <button onClick={confirmDelete} style={styles.confirmBtn}>Excluir</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  chatWindow: { display: 'flex', flexDirection: 'column', height: '80vh', border: '1px solid #ccc', borderRadius: '8px', overflow: 'hidden', position: 'relative' },
  header: { padding: '10px', backgroundColor: '#1A3B7D', color: 'white', display: 'flex', justifyContent: 'space-between' },
  status: { backgroundColor: '#28a745', padding: '2px 8px', borderRadius: '12px', fontSize: '12px' },
  messages: { flex: 1, overflowY: 'auto', padding: '10px', display: 'flex', flexDirection: 'column', backgroundColor: '#f1ebe3' },
  message: { maxWidth: '70%', marginBottom: '10px', padding: '8px', borderRadius: '8px', boxShadow: '0 1px 2px rgba(0,0,0,0.1)', position: 'relative' },
  messageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' },
  sender: { fontSize: '12px', fontWeight: 'bold' },
  deleteBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#dc3545', opacity: 0, transition: 'opacity 0.2s' },
  messageContent: { marginBottom: '4px' },
  time: { fontSize: '10px', textAlign: 'right', marginTop: '2px', color: '#666' },
  typing: { fontStyle: 'italic', color: '#666', padding: '5px' },
  inputArea: { display: 'flex', padding: '10px', borderTop: '1px solid #ccc', backgroundColor: '#f1ebe3', gap: '8px' },
  input: { flex: 1, padding: '8px', borderRadius: '12px', border: '1px solid #ccc', fontSize: '15px' },
  sendBtn: { padding: '8px 12px', backgroundColor: '#1A3B7D', color: 'white', border: 'none', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '38px', height: '38px' },
  attachBtn: { background: '#ddd', border: 'none', borderRadius: '4px', padding: '8px 12px', cursor: 'pointer', fontSize: '16px' },
  mediaImage: { maxWidth: '100%', maxHeight: '200px', borderRadius: '4px', marginTop: '4px' },
  mediaVideo: { maxWidth: '100%', maxHeight: '200px', borderRadius: '4px', marginTop: '4px' },
  mediaLink: { display: 'inline-block', marginTop: '4px', color: '#1A3B7D', textDecoration: 'none' },
  loading: { textAlign: 'center', padding: '20px' },
  toast: { position: 'absolute', bottom: '80px', left: '50%', transform: 'translateX(-50%)', color: 'white', padding: '8px 16px', borderRadius: '20px', fontSize: '14px', zIndex: 1000, boxShadow: '0 2px 5px rgba(0,0,0,0.2)' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 },
  modal: { backgroundColor: 'white', borderRadius: '8px', padding: '20px', width: '300px', textAlign: 'center' },
  modalButtons: { display: 'flex', justifyContent: 'space-around', marginTop: '20px' },
  cancelBtn: { background: '#6c757d', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' },
  confirmBtn: { background: '#dc3545', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' },
  pendingFile: {  display: 'flex',  alignItems: 'center',  justifyContent: 'space-between',  backgroundColor: '#e9ecef',  padding: '4px 8px',  borderRadius: '12px',  marginBottom: '4px',  fontSize: '12px',},
 removeFileBtn: {  background: 'none',  border: 'none',  cursor: 'pointer',  color: '#dc3545',  marginLeft: '8px',},
  dateSeparator: { display: 'flex', justifyContent: 'center', margin: '8px 0' },
  dateLabel: { backgroundColor: '#E1F3FB', color: '#4A90D9', padding: '4px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: '500', boxShadow: '0 1px 1px rgba(0,0,0,0.1)' },
  backButton: { background: 'transparent', border: 'none', color: 'white', fontSize: '16px', cursor: 'pointer', padding: '2px 4px', display: 'flex', alignItems: 'center', borderRadius: '4px' },
  searchInputHeader: { padding: '5px 10px 5px 28px', borderRadius: '12px', border: 'none', fontSize: '12px', backgroundColor: 'rgba(255,255,255,0.15)', color: '#fff', outline: 'none', width: '150px', placeholder: { color: '#fff' } },
  emojiBtn: { background: '#ddd', border: 'none', borderRadius: '4px', padding: '8px 12px', cursor: 'pointer', fontSize: '16px' },
  emojiPicker: { position: 'absolute', bottom: '48px', left: '-10px', backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.2)', zIndex: 100, overflow: 'hidden', width: '300px', maxWidth: '75vw' },
};

// Adicionar hover para mostrar botão de deletar (CSS-in-JS não suporta :hover no objeto, mas podemos usar className)
// Alternativa: adicionar regra global ou usar styled-components. Para simplicidade, usaremos className no elemento.
// No componente, adicione uma classe .message-item:hover .delete-btn { opacity: 1 }
// Como estamos usando estilo inline, uma solução rápida é usar uma tag <style> global no componente (não ideal, mas funcional)
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  .message-item:hover button {
    opacity: 1 !important;
  }
`;
document.head.appendChild(styleSheet);

export default ChatWindow;